import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  PlusCircleIcon,
  PencilSquareIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

// Audit log entry with initiative info
export interface RecentActivityEntry {
  id: string;
  action: 'created' | 'updated';
  fieldName: string | null;
  oldValue: string | null;
  newValue: string | null;
  changedAt: string;
  initiative: {
    id: string;
    title: string;
    subProduct: {
      id: string;
      name: string;
      product: {
        id: string;
        name: string;
      };
    };
  };
}

// Grouped entries
interface GroupedEntry {
  id: string;
  action: 'created' | 'updated';
  changedAt: string;
  initiative: RecentActivityEntry['initiative'];
  title?: string;
  changes?: {
    fieldName: string;
    oldValue: string | null;
    newValue: string | null;
  }[];
}

interface RecentChangesProps {
  entries: RecentActivityEntry[];
  loading?: boolean;
  onInitiativeClick?: (initiativeId: string) => void;
  maxVisible?: number;
}

// Format relative time
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

// Group entries by initiative and time (entries within 3 seconds are grouped)
function groupEntries(entries: RecentActivityEntry[]): GroupedEntry[] {
  const grouped: GroupedEntry[] = [];
  let currentGroup: GroupedEntry | null = null;
  let currentInitiativeId: string | null = null;
  let currentTime: number | null = null;

  for (const entry of entries) {
    const entryTime = new Date(entry.changedAt).getTime();

    if (entry.action === 'created') {
      grouped.push({
        id: entry.id,
        action: 'created',
        changedAt: entry.changedAt,
        initiative: entry.initiative,
        title: entry.newValue || entry.initiative.title,
      });
      currentGroup = null;
      currentInitiativeId = null;
      currentTime = null;
    } else {
      const shouldGroup =
        currentGroup &&
        currentInitiativeId === entry.initiative.id &&
        currentTime &&
        Math.abs(entryTime - currentTime) < 3000;

      if (shouldGroup && currentGroup) {
        currentGroup.changes?.push({
          fieldName: entry.fieldName || 'Unknown',
          oldValue: entry.oldValue,
          newValue: entry.newValue,
        });
      } else {
        currentGroup = {
          id: entry.id,
          action: 'updated',
          changedAt: entry.changedAt,
          initiative: entry.initiative,
          changes: [
            {
              fieldName: entry.fieldName || 'Unknown',
              oldValue: entry.oldValue,
              newValue: entry.newValue,
            },
          ],
        };
        grouped.push(currentGroup);
        currentInitiativeId = entry.initiative.id;
        currentTime = entryTime;
      }
    }
  }

  return grouped;
}

function ChangeRow({
  fieldName,
  oldValue,
  newValue,
}: {
  fieldName: string;
  oldValue: string | null;
  newValue: string | null;
}) {
  return (
    <div className="flex items-start gap-1.5 text-xs">
      <span className="text-gray-400">•</span>
      <span className="text-gray-500 font-medium">{fieldName}:</span>
      <div className="flex items-center gap-1 flex-wrap min-w-0">
        {oldValue && (
          <span className="text-gray-400 line-through truncate max-w-[80px]">{oldValue}</span>
        )}
        {oldValue && newValue && (
          <ArrowRightIcon className="w-2.5 h-2.5 text-gray-400 flex-shrink-0" />
        )}
        {newValue && <span className="text-gray-700 truncate max-w-[80px]">{newValue}</span>}
      </div>
    </div>
  );
}

function ActivityCard({
  entry,
  onClick,
}: {
  entry: GroupedEntry;
  onClick?: () => void;
}) {
  const isCreated = entry.action === 'created';

  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-all h-full flex flex-col"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex items-center gap-1.5 min-w-0">
          {isCreated ? (
            <PlusCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
          ) : (
            <PencilSquareIcon className="w-4 h-4 text-blue-500 flex-shrink-0" />
          )}
          <span className="text-xs text-gray-400 flex-shrink-0">
            {formatRelativeTime(entry.changedAt)}
          </span>
        </div>
      </div>

      {/* Initiative title */}
      <div className="text-sm font-medium text-gray-900 truncate mb-1">
        {entry.initiative.title}
      </div>

      {/* Product path */}
      <div className="text-xs text-gray-500 truncate mb-2">
        {entry.initiative.subProduct.product.name} &gt; {entry.initiative.subProduct.name}
      </div>

      {/* Changes or Created badge */}
      <div className="mt-auto">
        {isCreated ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
            Created
          </span>
        ) : entry.changes && entry.changes.length > 0 ? (
          <div className="space-y-0.5">
            {entry.changes.slice(0, 2).map((change, index) => (
              <ChangeRow
                key={index}
                fieldName={change.fieldName}
                oldValue={change.oldValue}
                newValue={change.newValue}
              />
            ))}
            {entry.changes.length > 2 && (
              <div className="text-xs text-gray-400">
                +{entry.changes.length - 2} more
              </div>
            )}
          </div>
        ) : null}
      </div>
    </button>
  );
}

export function RecentChanges({
  entries,
  loading,
  onInitiativeClick,
  maxVisible = 9,
}: RecentChangesProps) {
  const groupedEntries = useMemo(() => groupEntries(entries), [entries]);
  const visibleEntries = groupedEntries.slice(0, maxVisible);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">
          Recent Activity
        </h3>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900">
          Recent Activity
        </h3>
        <Link
          to="/auditlog"
          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
        >
          View All
        </Link>
      </div>

      {visibleEntries.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mb-2">
            <PencilSquareIcon className="w-5 h-5 text-gray-400" />
          </div>
          <p className="text-sm text-gray-500">No recent activity</p>
          <p className="text-xs text-gray-400 mt-0.5">
            Changes to initiatives will appear here
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {visibleEntries.map((entry) => (
            <ActivityCard
              key={entry.id}
              entry={entry}
              onClick={() => onInitiativeClick?.(entry.initiative.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
